import React, { useEffect, useState } from "react";
import BlogCard from "./BlogCard";
import BlogPostOverlay from "./BlogPostOverlay";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useI18nField } from "../locale/LocaleContext";

const BlogList = ({ collectionName }) => {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const f = useI18nField();
  const tr = (en, fr) => f({ name: { en, fr } }, "name");

  // Make sure any text field can be bilingual or a plain string
  const normalizeI18n = (v) =>
    typeof v === "string" ? { en: v, fr: v } : v;

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, collectionName));
        const raw = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Normalize + translate to strings the fields your cards/overlay use
        const hydrated = raw.map((item) => {
          const nItem = { ...item };

          // Normalize potentially bilingual fields
          if (nItem.title) nItem.title = normalizeI18n(nItem.title);
          if (nItem.subtitle) nItem.subtitle = normalizeI18n(nItem.subtitle);
          if (nItem.excerpt) nItem.excerpt = normalizeI18n(nItem.excerpt);
          if (nItem.content) nItem.content = normalizeI18n(nItem.content);
          if (nItem.imageAlt) nItem.imageAlt = normalizeI18n(nItem.imageAlt);

          // Compute translated strings (override keys with the active-locale string)
          const titleStr = nItem.title ? f(nItem, "title") : "";
          const subtitleStr = nItem.subtitle ? f(nItem, "subtitle") : "";
          const excerptStr = nItem.excerpt ? f(nItem, "excerpt") : "";
          const contentStr = nItem.content ? f(nItem, "content") : "";
          const imageAltStr = nItem.imageAlt ? f(nItem, "imageAlt") : "";

          // Keep raw bilingual values if you ever need them later
          return {
            ...nItem,
            _rawI18n: {
              title: nItem.title,
              subtitle: nItem.subtitle,
              excerpt: nItem.excerpt,
              content: nItem.content,
              imageAlt: nItem.imageAlt,
            },
            // Override with strings so components can use them directly
            title: titleStr,
            subtitle: subtitleStr,
            excerpt: excerptStr,
            content: contentStr,
            imageAlt: imageAltStr,
          };
        });

        setPosts(hydrated);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching blog posts:", err);
        setError(tr("Failed to fetch blog posts.", "Échec du chargement des articles."));
        setLoading(false);
      }
    };

    fetchPosts();
  }, [collectionName]); // language switching should be handled by re-rendering f()

  const handleSelectPost = (post) => setSelectedPost(post);
  const handleCloseOverlay = () => setSelectedPost(null);

  if (loading) return <div>{tr("Loading…", "Chargement…")}</div>;
  if (error) return <div>{tr("Error:", "Erreur :")} {error}</div>;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-10">
        {posts.map((post) => (
          <BlogCard
            key={post.id}
            post={post}                 // now has title/excerpt/content as strings in active locale
            onSelectPost={handleSelectPost}
          />
        ))}
      </div>
      {selectedPost && (
        <BlogPostOverlay
          post={selectedPost}          // content is already the translated string
          onClose={handleCloseOverlay}
        />
      )}
    </>
  );
};

export default BlogList;
